// src/pages/WpManagement.js — Client WordPress Management (2026-08-31)
// Staff console for web-dev / WP-admin tasks on client WordPress sites.
// A site is enrolled by installing the MIDTC Manage plugin on it and pasting
// the per-site API key (shown once on Add Site) into the plugin's settings.
import { useState, useEffect, useCallback } from "react";
import WpDevConsole from "../components/WpDevConsole";
import WpFileManager from "../components/WpFileManager";

const API = "https://ticketing.midtc.com/api";
const tok = () => localStorage.getItem("midtc_token") || "";
const apiFetch = (path, opts = {}) =>
  fetch(API + path, { headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" }, ...opts });

const fmtDT = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never";

export default function WpManagement() {
  const [sites, setSites]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive]   = useState(null);      // selected site
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey]   = useState(null);      // {site, api_key} shown once after create

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/wp/sites");
      const d = await r.json();
      setSites(Array.isArray(d) ? d : []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPlugin = async () => {
    const r = await apiFetch("/wp/plugin-download");
    if (!r.ok) { alert("Plugin zip not found on the server"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (r.headers.get("content-disposition") || "").match(/filename="?([^";]+)/)?.[1] || "midtc-manage.zip";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const removeSite = async (id) => {
    if (!window.confirm("Remove this site from WP Management? (The plugin stays installed on the site.)")) return;
    await apiFetch(`/wp/sites/${id}`, { method: "DELETE" });
    if (active?.id === id) setActive(null);
    load();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden", background: "var(--t-page)" }}>
      <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid var(--t-border-soft)", flexShrink: 0, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--t-text-primary)", letterSpacing: "-0.5px" }}>WP Management</h1>
          <p style={{ margin: "3px 0 0", color: "var(--t-text-faint)", fontSize: 13 }}>{sites.length} WordPress site{sites.length !== 1 ? "s" : ""} enrolled via the MIDTC Manage plugin</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={downloadPlugin}
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "7px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            ⬇ Download plugin
          </button>
          <button onClick={() => setShowAdd(true)}
            style={{ background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            + Add Site
          </button>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {/* Sites list */}
        <div style={{ width: 300, flexShrink: 0, borderRight: "1px solid var(--t-border-soft)", overflow: "auto", background: "var(--t-panel-hi)" }}>
          {loading && <div style={{ padding: 20, color: "#475569", fontSize: 13 }}>Loading…</div>}
          {!loading && !sites.length && (
            <div style={{ padding: 28, textAlign: "center", color: "#475569", fontSize: 12.5, lineHeight: 1.7 }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>🌐</div>
              No sites enrolled yet.<br />
              <b>1.</b> Add Site (you get an API key)<br />
              <b>2.</b> Install the MIDTC Manage plugin on the WP site<br />
              <b>3.</b> Paste the key in Settings → MIDTC Manage
            </div>
          )}
          {sites.map(s => {
            const h = s.last_health || {};
            const updates = (h.plugin_updates || 0) + (h.theme_updates || 0) + (h.core_update ? 1 : 0);
            return (
              <div key={s.id} onClick={() => setActive(s)}
                style={{ padding: "12px 14px", cursor: "pointer", borderBottom: "1px solid var(--t-border-soft)", background: active?.id === s.id ? "var(--t-surface-mid)" : "transparent", borderLeft: `3px solid ${active?.id === s.id ? "#2563eb" : "transparent"}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: active?.id === s.id ? "#93c5fd" : "var(--t-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s.label || s.site_url.replace(/^https?:\/\//, "")}
                </div>
                {s.client_name && <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{s.client_name}</div>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 5 }}>
                  <span style={{ fontSize: 10, color: s.last_seen_at ? "#34d399" : "#64748b" }}>
                    {s.last_seen_at ? `● seen ${fmtDT(s.last_seen_at)}` : "○ never contacted"}
                  </span>
                  {updates > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#fbbf24", background: "rgba(251,191,36,0.12)", borderRadius: 99, padding: "1px 7px" }}>{updates} update{updates > 1 ? "s" : ""}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Site console */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {!active ? (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, color: "#475569" }}>
              <div style={{ fontSize: 44 }}>🌐</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b" }}>Select a site to manage it</div>
            </div>
          ) : (
            <SiteConsole key={active.id} site={active} onRemove={() => removeSite(active.id)} onChanged={load} />
          )}
        </div>
      </div>

      {showAdd && <AddSiteModal onClose={() => setShowAdd(false)} onCreated={(site) => { setShowAdd(false); setNewKey(site); load(); }} />}
      {newKey && <KeyModal site={newKey} onClose={() => setNewKey(null)} />}
    </div>
  );
}

// ─── Per-site console ────────────────────────────────────────────────────────
function SiteConsole({ site, onRemove, onChanged }) {
  const [tab, setTab]     = useState("overview");
  const [info, setInfo]   = useState(null);
  const [err, setErr]     = useState("");
  const [busy, setBusy]   = useState("");        // action currently running
  const [plugins, setPlugins] = useState(null);
  const [users, setUsers]     = useState(null);
  const [updates, setUpdates] = useState(null);
  const [credential, setCredential] = useState(null); // {login, password} shown once
  const [installSlug, setInstallSlug] = useState("");
  const [newUser, setNewUser] = useState({ username: "", email: "", role: "editor" });
  const [devCfg, setDevCfg] = useState(null);

  const loadDevCfg = useCallback(() => {
    apiFetch(`/wp/sites/${site.id}/dev-config`).then(r => r.json()).then(setDevCfg).catch(() => {});
  }, [site.id]);
  useEffect(() => { loadDevCfg(); }, [loadDevCfg]);

  const act = useCallback(async (action, params = {}, label = action) => {
    setBusy(label); setErr("");
    try {
      const r = await apiFetch(`/wp/sites/${site.id}/action`, { method: "POST", body: JSON.stringify({ action, params }) });
      const d = await r.json();
      if (!r.ok || d.success === false) { setErr(d.error || "Action failed"); return null; }
      return d.data ?? d;
    } catch (e) { setErr(String(e.message || e)); return null; }
    finally { setBusy(""); }
  }, [site.id]);

  const refreshInfo = useCallback(async () => {
    const d = await act("info", {}, "info");
    if (d) { setInfo(d); onChanged(); }
  }, [act, onChanged]);

  useEffect(() => { refreshInfo(); /* eslint-disable-next-line */ }, [site.id]);
  useEffect(() => {
    if (tab === "plugins" && !plugins) act("plugins_list", {}, "plugins").then(d => d && setPlugins(d));
    if (tab === "users"   && !users)   act("users_list", {}, "users").then(d => d && setUsers(d));
    if (tab === "updates" && !updates) act("updates_list", {}, "updates").then(d => d && setUpdates(d));
    // eslint-disable-next-line
  }, [tab]);

  const reloadPlugins = () => act("plugins_list", {}, "plugins").then(d => d && setPlugins(d));

  const btn = (label, onClick, opts = {}) => (
    <button onClick={onClick} disabled={!!busy}
      style={{ background: opts.danger ? "rgba(239,68,68,0.08)" : "var(--t-panel)", border: `1px solid ${opts.danger ? "rgba(239,68,68,0.25)" : "var(--t-border-soft)"}`,
        color: opts.danger ? "#f87171" : "#94a3b8", borderRadius: 7, padding: "6px 13px", fontSize: 12, fontWeight: 600,
        cursor: busy ? "default" : "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1 }}>
      {busy === (opts.busyKey || label) ? "…" : label}
    </button>
  );

  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10, padding: 14 };

  return (
    <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Site header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "var(--t-text-primary)" }}>{site.label || site.site_url}</div>
          <a href={site.site_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#60a5fa" }}>{site.site_url} ↗</a>
          {" · "}
          <a href={`${site.site_url}/wp-admin/`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#60a5fa" }}>wp-admin ↗</a>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {btn("↺ Refresh", refreshInfo, { busyKey: "info" })}
          {btn("Remove site", onRemove, { danger: true })}
        </div>
      </div>

      {err && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171", borderRadius: 8, padding: "9px 13px", fontSize: 12.5 }}>{err}</div>}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--t-border-soft)" }}>
        {[["overview", "Overview"], ["plugins", "Plugins"], ["users", "Users"], ["updates", "Updates"],
          ["console", "⌨ Dev Console"], ["files", "📁 File Manager"], ["connection", "⚙ Connection"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: "8px 16px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit",
              borderBottom: `2px solid ${tab === k ? "#3b82f6" : "transparent"}`, color: tab === k ? "#60a5fa" : "#64748b" }}>{l}</button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && (
        <>
          {!info && !err && <div style={{ color: "#475569", fontSize: 13 }}>Contacting site…</div>}
          {info && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                {[
                  ["WordPress", info.wp_version + (info.core_update ? ` → ${info.core_update}` : ""), info.core_update ? "#fbbf24" : "#34d399"],
                  ["PHP", info.php_version, "#94a3b8"],
                  ["Plugins", `${info.plugins_active}/${info.plugins_total} active`, "#94a3b8"],
                  ["Plugin updates", info.plugin_updates, info.plugin_updates > 0 ? "#fbbf24" : "#34d399"],
                  ["Users", info.users_total, "#94a3b8"],
                  ["HTTPS", info.https ? "yes" : "NO", info.https ? "#34d399" : "#f87171"],
                  ["Maintenance", info.maintenance ? "ON" : "off", info.maintenance ? "#f87171" : "#34d399"],
                  ["Debug mode", info.debug_mode ? "ON" : "off", info.debug_mode ? "#fbbf24" : "#34d399"],
                ].map(([l, v, c]) => (
                  <div key={l} style={panel}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: c }}>{String(v)}</div>
                    <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ ...panel, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 4 }}>Quick actions</span>
                {btn("🧹 Clear caches", () => act("clear_cache", {}, "clear_cache").then(d => d && refreshInfo()), { busyKey: "clear_cache" })}
                {info.maintenance
                  ? btn("Maintenance OFF", () => act("maintenance_off", {}, "maint").then(d => d && refreshInfo()), { busyKey: "maint" })
                  : btn("🚧 Maintenance ON", () => act("maintenance_on", {}, "maint").then(d => d && refreshInfo()), { busyKey: "maint" })}
                {info.core_update && btn(`⬆ Update core → ${info.core_update}`, () => window.confirm(`Update WordPress core to ${info.core_update}?`) && act("core_update", {}, "core").then(d => d && refreshInfo()), { busyKey: "core" })}
              </div>
              <div style={{ fontSize: 11, color: "#475569" }}>Theme: {info.theme} · Site: {info.site_title} · TZ: {info.timezone} · MIDTC Manage v{info.plugin_version}</div>
            </>
          )}
        </>
      )}

      {/* ── Plugins ── */}
      {tab === "plugins" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input value={installSlug} onChange={e => setInstallSlug(e.target.value)} placeholder="Install from wordpress.org — plugin slug (e.g. wordfence)"
              style={{ flex: 1, background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 11px", color: "var(--t-text-primary)", fontSize: 12.5 }} />
            {btn("Install", () => installSlug.trim() && act("plugin_install", { slug: installSlug.trim() }, "install").then(d => { if (d) { setInstallSlug(""); reloadPlugins(); } }), { busyKey: "install" })}
          </div>
          {!plugins && <div style={{ color: "#475569", fontSize: 13 }}>Loading plugins…</div>}
          {plugins && plugins.map(p => (
            <div key={p.file} style={{ ...panel, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>
                  {p.name} <span style={{ fontWeight: 400, color: "#64748b", fontSize: 11.5 }}>v{p.version}</span>
                  {p.update && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: "#fbbf24", background: "rgba(251,191,36,0.12)", borderRadius: 99, padding: "1px 8px" }}>update → {p.update}</span>}
                </div>
                <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{p.description}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: p.active ? "#34d399" : "#64748b" }}>{p.active ? "● active" : "○ inactive"}</span>
              {p.update && btn("Update", () => act("plugin_update", { plugin: p.file }, `upd:${p.file}`).then(d => d && reloadPlugins()), { busyKey: `upd:${p.file}` })}
              {p.active
                ? btn("Deactivate", () => act("plugin_deactivate", { plugin: p.file }, `tog:${p.file}`).then(d => d && reloadPlugins()), { busyKey: `tog:${p.file}` })
                : btn("Activate", () => act("plugin_activate", { plugin: p.file }, `tog:${p.file}`).then(d => d && reloadPlugins()), { busyKey: `tog:${p.file}` })}
              {!p.active && btn("Delete", () => window.confirm(`Delete ${p.name} from the site?`) && act("plugin_delete", { plugin: p.file }, `del:${p.file}`).then(d => d && reloadPlugins()), { danger: true, busyKey: `del:${p.file}` })}
            </div>
          ))}
        </>
      )}

      {/* ── Users ── */}
      {tab === "users" && (
        <>
          <div style={{ ...panel, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600 }}>Username</label>
              <input value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 9px", color: "var(--t-text-primary)", fontSize: 12, width: "100%", boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1.4, minWidth: 150 }}>
              <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600 }}>Email</label>
              <input value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 9px", color: "var(--t-text-primary)", fontSize: 12, width: "100%", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600 }}>Role</label>
              <select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))} style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 6, padding: "6px 9px", color: "var(--t-text-primary)", fontSize: 12 }}>
                {["administrator", "editor", "author", "contributor", "subscriber", "shop_manager"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            {btn("＋ Create user", () => newUser.username && newUser.email &&
              act("user_create", newUser, "user_create").then(d => {
                if (d) { setCredential(d); setNewUser({ username: "", email: "", role: "editor" }); act("users_list", {}, "users").then(x => x && setUsers(x)); }
              }), { busyKey: "user_create" })}
          </div>
          {!users && <div style={{ color: "#475569", fontSize: 13 }}>Loading users…</div>}
          {users && users.map(u => (
            <div key={u.id} style={{ ...panel, padding: "9px 14px", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>{u.login}</span>
                <span style={{ fontSize: 11.5, color: "#64748b", marginLeft: 8 }}>{u.email}</span>
              </div>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{(u.roles || []).join(", ")}</span>
              {btn("Reset password", () => window.confirm(`Reset the password for "${u.login}"?`) &&
                act("user_reset_password", { user_id: u.id }, `rp:${u.id}`).then(d => d && setCredential(d)), { busyKey: `rp:${u.id}` })}
            </div>
          ))}
        </>
      )}

      {/* ── Updates ── */}
      {tab === "updates" && (
        <>
          {!updates && <div style={{ color: "#475569", fontSize: 13 }}>Checking for updates…</div>}
          {updates && (
            <>
              {updates.core && (
                <div style={{ ...panel, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 13, color: "var(--t-text-primary)", fontWeight: 700 }}>WordPress core {updates.core.current} → {updates.core.new}</div>
                  {btn("Update core", () => window.confirm(`Update WordPress core to ${updates.core.new}?`) && act("core_update", {}, "core").then(d => d && act("updates_list", {}, "updates").then(x => x && setUpdates(x))), { busyKey: "core" })}
                </div>
              )}
              {(updates.plugins || []).map(p => (
                <div key={p.file} style={{ ...panel, padding: "9px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 12.5, color: "var(--t-text-body)" }}><b>{p.name}</b> {p.current} → <span style={{ color: "#fbbf24" }}>{p.new}</span></div>
                  {btn("Update", () => act("plugin_update", { plugin: p.file }, `upd:${p.file}`).then(d => d && act("updates_list", {}, "updates").then(x => x && setUpdates(x))), { busyKey: `upd:${p.file}` })}
                </div>
              ))}
              {!updates.core && !(updates.plugins || []).length && !(updates.themes || []).length && (
                <div style={{ textAlign: "center", padding: 30, color: "#34d399", fontSize: 13 }}>✓ Everything is up to date</div>
              )}
              {(updates.themes || []).length > 0 && (
                <div style={{ fontSize: 12, color: "#94a3b8" }}>Theme updates available: {updates.themes.map(t => t.slug).join(", ")} (update via wp-admin)</div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Dev Console (SSH) ── */}
      {tab === "console" && (
        !devCfg?.dev_enabled
          ? <DevDisabledNotice onGo={() => setTab("connection")} />
          : <WpDevConsole siteId={site.id} sshConfigured={!!devCfg?.ssh_host} />
      )}

      {/* ── File Manager (SFTP / FTP) ── */}
      {tab === "files" && (
        !devCfg?.dev_enabled
          ? <DevDisabledNotice onGo={() => setTab("connection")} />
          : (devCfg?.fm_protocol === "ftp" ? !!devCfg?.ftp_host : !!devCfg?.ssh_host)
            ? <WpFileManager siteId={site.id} protocol={devCfg.fm_protocol} fileRoot={devCfg.file_root} />
            : <div style={{ padding: 30, textAlign: "center", color: "#64748b", fontSize: 13 }}>
                {devCfg?.fm_protocol === "ftp" ? "FTP" : "SFTP/SSH"} isn't configured for this site — add it under <b>Connection</b>.
              </div>
      )}

      {/* ── Connection settings ── */}
      {tab === "connection" && (
        <ConnectionTab siteId={site.id} cfg={devCfg} onSaved={loadDevCfg} />
      )}

      {credential && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}>
          <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 14, padding: 24, maxWidth: 420, width: "100%" }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 15, color: "var(--t-text-primary)" }}>Credentials — shown once</h3>
            <p style={{ fontSize: 12.5, color: "#94a3b8", margin: "0 0 14px" }}>Copy these now; the password is not stored anywhere.</p>
            <div style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 8, padding: 14, fontFamily: "monospace", fontSize: 14, color: "var(--t-text-primary)" }}>
              <div>user: <b>{credential.login}</b></div>
              <div>pass: <b>{credential.password}</b></div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button onClick={() => navigator.clipboard?.writeText(`${credential.login} / ${credential.password}`)}
                style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Copy</button>
              <button onClick={() => setCredential(null)}
                style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Shown on the Dev Console / File Manager tabs until dev tools are switched on
// for the site — the tools stay off by default so a plain WP-plugin site never
// carries SSH credentials it doesn't need.
function DevDisabledNotice({ onGo }) {
  return (
    <div style={{ padding: 34, textAlign: "center", color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>
      <div style={{ fontSize: 30, marginBottom: 8 }}>🔒</div>
      Dev tools are <b>off</b> for this site.<br />
      Turn them on and add SSH/FTP details under Connection.
      <div style={{ marginTop: 14 }}>
        <button onClick={onGo}
          style={{ background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color: "#fff", border: "none", borderRadius: 7, padding: "8px 18px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Open Connection settings
        </button>
      </div>
    </div>
  );
}

// ─── Connection settings (SSH / FTP credentials) ─────────────────────────────
function ConnectionTab({ siteId, cfg, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const [note, setNote] = useState(null);

  useEffect(() => {
    if (!cfg) return;
    setF({
      dev_enabled: !!cfg.dev_enabled, fm_protocol: cfg.fm_protocol || "sftp", file_root: cfg.file_root || "",
      ssh_host: cfg.ssh_host || "", ssh_port: cfg.ssh_port || 22, ssh_user: cfg.ssh_user || "", ssh_auth: cfg.ssh_auth || "password",
      ssh_secret: "", ssh_passphrase: "",
      ftp_host: cfg.ftp_host || "", ftp_port: cfg.ftp_port || 21, ftp_user: cfg.ftp_user || "", ftp_password: "", ftp_secure: !!cfg.ftp_secure,
    });
  }, [cfg]);

  if (!f) return <div style={{ padding: 20, color: "#475569", fontSize: 13 }}>Loading…</div>;

  const save = async (extra = {}) => {
    setSaving(true); setNote(null);
    try {
      const body = { ...f, ...extra };
      // Don't send blank secrets — blank means "leave the stored one alone".
      if (!body.ssh_secret) delete body.ssh_secret;
      if (!body.ssh_passphrase) delete body.ssh_passphrase;
      if (!body.ftp_password) delete body.ftp_password;
      const r = await apiFetch(`/wp/sites/${siteId}/dev-config`, { method: "PUT", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setNote({ ok: false, text: d.error || "Save failed" }); return; }
      setNote({ ok: true, text: "Saved ✓" });
      setF(p => ({ ...p, ssh_secret: "", ssh_passphrase: "", ftp_password: "" }));
      onSaved();
    } finally { setSaving(false); }
  };

  const test = async (which) => {
    setTesting(which); setNote(null);
    try {
      const r = await apiFetch(`/wp/sites/${siteId}/dev-config/test`, { method: "POST", body: JSON.stringify({ which }) });
      const d = await r.json();
      setNote({ ok: r.ok, text: r.ok ? d.detail : d.error });
    } finally { setTesting(""); }
  };

  const ipt = { background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 10px", color: "var(--t-text-primary)", fontSize: 12.5, width: "100%", boxSizing: "border-box" };
  const lbl = { fontSize: 10.5, color: "#64748b", display: "block", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" };
  const panel = { background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 };
  const btn = (primary) => ({ background: primary ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-panel)", color: primary ? "#fff" : "#94a3b8", border: primary ? "none" : "1px solid var(--t-border-soft)", borderRadius: 7, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {note && (
        <div style={{ background: note.ok ? "rgba(52,211,153,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${note.ok ? "rgba(52,211,153,0.3)" : "rgba(239,68,68,0.3)"}`, color: note.ok ? "#34d399" : "#f87171", borderRadius: 8, padding: "9px 13px", fontSize: 12.5 }}>{note.text}</div>
      )}

      <div style={{ ...panel, flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
          <input type="checkbox" checked={f.dev_enabled} onChange={e => { const v = e.target.checked; setF(p => ({ ...p, dev_enabled: v })); save({ dev_enabled: v }); }} style={{ accentColor: "#4f6fff" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--t-text-primary)" }}>Enable dev tools (SSH console + file manager)</span>
        </label>
        <span style={{ fontSize: 11.5, color: "#64748b" }}>Off by default. Admin / manager / tech / dev only, and every action is audit-logged.</span>
      </div>

      <div style={panel}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>SSH — Dev Console &amp; SFTP</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 1.4fr", gap: 8 }}>
          <div><label style={lbl}>Host</label><input value={f.ssh_host} onChange={e => setF(p => ({ ...p, ssh_host: e.target.value }))} placeholder="203.0.113.10 or ssh.host.com" style={ipt} /></div>
          <div><label style={lbl}>Port</label><input value={f.ssh_port} onChange={e => setF(p => ({ ...p, ssh_port: e.target.value }))} style={ipt} /></div>
          <div><label style={lbl}>User</label><input value={f.ssh_user} onChange={e => setF(p => ({ ...p, ssh_user: e.target.value }))} placeholder="deploy" style={ipt} /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 8 }}>
          <div><label style={lbl}>Auth</label>
            <select value={f.ssh_auth} onChange={e => setF(p => ({ ...p, ssh_auth: e.target.value }))} style={ipt}>
              <option value="password">Password</option><option value="key">Private key</option>
            </select></div>
          <div>
            <label style={lbl}>{f.ssh_auth === "key" ? "Private key (PEM)" : "Password"} {cfg.has_ssh_secret && <span style={{ color: "#34d399", textTransform: "none" }}>· stored — leave blank to keep</span>}</label>
            {f.ssh_auth === "key"
              ? <textarea rows={3} value={f.ssh_secret} onChange={e => setF(p => ({ ...p, ssh_secret: e.target.value }))} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" style={{ ...ipt, fontFamily: "monospace", fontSize: 11 }} />
              : <input type="password" value={f.ssh_secret} onChange={e => setF(p => ({ ...p, ssh_secret: e.target.value }))} autoComplete="new-password" style={ipt} />}
          </div>
        </div>
        {f.ssh_auth === "key" && (
          <div><label style={lbl}>Key passphrase {cfg.has_ssh_passphrase && <span style={{ color: "#34d399", textTransform: "none" }}>· stored</span>}</label>
            <input type="password" value={f.ssh_passphrase} onChange={e => setF(p => ({ ...p, ssh_passphrase: e.target.value }))} autoComplete="new-password" style={ipt} /></div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, color: cfg.has_host_key ? "#34d399" : "#64748b" }}>
            {cfg.has_host_key ? "🔒 Host key pinned — a change will block the connection" : "Host key will be pinned on first connect"}
          </span>
          {cfg.has_host_key && (
            <button onClick={() => window.confirm("Reset the pinned SSH host key? Only do this if the server was legitimately rebuilt — otherwise a mismatch may mean interception.") && save({ reset_host_key: true })}
              style={{ ...btn(false), padding: "4px 11px", fontSize: 11.5 }}>Reset host key</button>
          )}
          <button onClick={() => test("ssh")} style={{ ...btn(false), marginLeft: "auto" }}>{testing === "ssh" ? "Testing…" : "Test SSH"}</button>
        </div>
      </div>

      <div style={panel}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>FTP — alternative for hosts without SSH</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 70px 1.4fr", gap: 8 }}>
          <div><label style={lbl}>Host</label><input value={f.ftp_host} onChange={e => setF(p => ({ ...p, ftp_host: e.target.value }))} style={ipt} /></div>
          <div><label style={lbl}>Port</label><input value={f.ftp_port} onChange={e => setF(p => ({ ...p, ftp_port: e.target.value }))} style={ipt} /></div>
          <div><label style={lbl}>User</label><input value={f.ftp_user} onChange={e => setF(p => ({ ...p, ftp_user: e.target.value }))} style={ipt} /></div>
        </div>
        <div><label style={lbl}>Password {cfg.has_ftp_password && <span style={{ color: "#34d399", textTransform: "none" }}>· stored</span>}</label>
          <input type="password" value={f.ftp_password} onChange={e => setF(p => ({ ...p, ftp_password: e.target.value }))} autoComplete="new-password" style={ipt} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12, color: "var(--t-text-body)" }}>
            <input type="checkbox" checked={f.ftp_secure} onChange={e => setF(p => ({ ...p, ftp_secure: e.target.checked }))} style={{ accentColor: "#4f6fff" }} />
            Use FTPS (TLS)
          </label>
          {!f.ftp_secure && f.ftp_host && <span style={{ fontSize: 11.5, color: "#fbbf24" }}>⚠ Plain FTP sends the password in clear text — prefer SFTP or FTPS.</span>}
          <button onClick={() => test("ftp")} style={{ ...btn(false), marginLeft: "auto" }}>{testing === "ftp" ? "Testing…" : "Test FTP"}</button>
        </div>
      </div>

      <div style={panel}>
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 10 }}>
          <div><label style={lbl}>File manager uses</label>
            <select value={f.fm_protocol} onChange={e => setF(p => ({ ...p, fm_protocol: e.target.value }))} style={ipt}>
              <option value="sftp">SFTP (over SSH)</option><option value="ftp">FTP</option>
            </select></div>
          <div><label style={lbl}>Root directory (confines the file manager)</label>
            <input value={f.file_root} onChange={e => setF(p => ({ ...p, file_root: e.target.value }))} placeholder="/home/master/applications/abc/public_html" style={ipt} /></div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => save()} disabled={saving} style={btn(true)}>{saving ? "Saving…" : "Save connection"}</button>
      </div>
    </div>
  );
}

// ─── Add Site modal ──────────────────────────────────────────────────────────
function AddSiteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ site_url: "", label: "", cdb_client_id: "" });
  const [cdbClients, setCdbClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    apiFetch("/client-database/clients").then(r => r.json()).then(d => setCdbClients(Array.isArray(d) ? d : (d.clients || []))).catch(() => {});
  }, []);

  const ipt = { background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 11px", color: "var(--t-text-primary)", fontSize: 12.5, width: "100%", boxSizing: "border-box" };

  const save = async () => {
    if (!/^https?:\/\/\S+/.test(form.site_url)) { setErr("Enter the site URL including https://"); return; }
    setSaving(true);
    try {
      const r = await apiFetch("/wp/sites", { method: "POST", body: JSON.stringify({ ...form, cdb_client_id: form.cdb_client_id || null }) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || "Failed"); return; }
      onCreated(d);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 14, width: "100%", maxWidth: 460 }}>
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid var(--t-border-soft)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--t-text-primary)" }}>Add WordPress Site</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
          {err && <div style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", borderRadius: 7, padding: "8px 12px", fontSize: 12.5 }}>{err}</div>}
          <div>
            <label style={{ fontSize: 11.5, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Site URL *</label>
            <input value={form.site_url} onChange={e => setForm(f => ({ ...f, site_url: e.target.value }))} placeholder="https://clientsite.com" style={ipt} />
          </div>
          <div>
            <label style={{ fontSize: 11.5, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Label</label>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Acme Corp — main site" style={ipt} />
          </div>
          <div>
            <label style={{ fontSize: 11.5, color: "#94a3b8", display: "block", marginBottom: 4, fontWeight: 600 }}>Registry client</label>
            <select value={form.cdb_client_id} onChange={e => setForm(f => ({ ...f, cdb_client_id: e.target.value }))} style={ipt}>
              <option value="">— none —</option>
              {cdbClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 11.5, color: "#64748b", background: "rgba(79,111,255,0.06)", border: "1px solid rgba(79,111,255,0.15)", borderRadius: 7, padding: "8px 11px", lineHeight: 1.6 }}>
            After adding you'll get a one-time <b>API key</b>. Install the MIDTC Manage plugin on the site
            (Download plugin → Plugins → Add New → Upload), then paste the key under <b>Settings → MIDTC Manage</b>.
          </div>
        </div>
        <div style={{ padding: "12px 22px 16px", borderTop: "1px solid var(--t-border-soft)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "none", border: "1px solid var(--t-border-soft)", borderRadius: 7, padding: "8px 18px", color: "#64748b", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "8px 20px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {saving ? "Adding…" : "Add Site"}
          </button>
        </div>
      </div>
    </div>
  );
}

// One-time API-key reveal after creating a site
function KeyModal({ site, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}>
      <div style={{ background: "var(--t-panel-hi)", border: "1px solid var(--t-border-soft)", borderRadius: 14, padding: 24, maxWidth: 520, width: "100%" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, color: "var(--t-text-primary)" }}>🔑 API key for {site.label || site.site_url}</h3>
        <p style={{ fontSize: 12.5, color: "#94a3b8", margin: "0 0 12px", lineHeight: 1.6 }}>
          Paste this into <b>Settings → MIDTC Manage</b> on the WordPress site. Admins can re-reveal or rotate it later; treat it like a password.
        </p>
        <div style={{ background: "var(--t-input)", border: "1px solid var(--t-border-soft)", borderRadius: 8, padding: 14, fontFamily: "monospace", fontSize: 13, color: "var(--t-text-primary)", wordBreak: "break-all" }}>
          {site.api_key}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={() => navigator.clipboard?.writeText(site.api_key)}
            style={{ background: "var(--t-panel)", border: "1px solid var(--t-border-soft)", color: "#94a3b8", borderRadius: 7, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Copy key</button>
          <button onClick={onClose}
            style={{ background: "#4f6fff", color: "#fff", border: "none", borderRadius: 7, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
        </div>
      </div>
    </div>
  );
}
